import { createContext, useCallback, useContext, useMemo, useRef } from "react";

interface KlausurEditorScrollContextValue {
	/** Register a DOM element by id for later scrolling */
	registerElement: (id: string, el: HTMLElement | null) => void;
	/** Smoothly scroll to an element previously registered */
	scrollToElement: (
		id: string,
		behavior?: "smooth" | "auto" | "instant",
	) => void;
}

const KlausurEditorScrollContext = createContext<
	KlausurEditorScrollContextValue | undefined
>(undefined);

interface KlausurEditorScrollProviderProps {
	children: React.ReactNode;
}

/**
 * KlausurEditorScrollProvider
 *
 * Keeps a registry of DOM elements by stable ids (e.g. musterloesungId/itemId)
 * and exposes a simple API to scroll to them. Components call registerElement
 * on mount with their corresponding id, and the provider remembers the element
 * reference. After creating new entities, callers can request a scroll by id.
 */
export function KlausurEditorScrollProvider({
	children,
}: KlausurEditorScrollProviderProps) {
	const registryRef = useRef(new Map<string, HTMLElement>());

	/** Register or unregister an element under a stable id */
	const registerElement = useCallback((id: string, el: HTMLElement | null) => {
		const map = registryRef.current;
		if (!el) {
			map.delete(id);
			return;
		}
		map.set(id, el);
	}, []);

	/** Smooth scroll the container to the element with the given id */
	const scrollToElement = useCallback(
		(id: string, behavior: "smooth" | "auto" | "instant" = "smooth") => {
			const doScroll = () => {
				let el: HTMLElement | null | undefined = registryRef.current.get(id);
				if (!el) {
					// Fallback: query by data attribute if not explicitly registered
					el = document.querySelector(
						`[data-scroll-id="${id}"]`,
					) as HTMLElement | null;
				}
				if (!el) return;
				try {
					el.scrollIntoView({ behavior, block: "center" });
				} catch {
					el.scrollIntoView();
				}
			};
			// Defer to the next tick to ensure DOM is updated after state changes
			setTimeout(doScroll, 0);
		},
		[],
	);

	const value = useMemo(
		() => ({ registerElement, scrollToElement }),
		[registerElement, scrollToElement],
	);

	return (
		<KlausurEditorScrollContext.Provider value={value}>
			{children}
		</KlausurEditorScrollContext.Provider>
	);
}

export function useKlausurEditorScroll() {
	const ctx = useContext(KlausurEditorScrollContext);
	if (!ctx)
		throw new Error(
			"useKlausurEditorScroll must be used within KlausurEditorScrollProvider",
		);
	return ctx;
}
